import { TeamBrandingSectionView } from "@cma/presentation-generation-logic/models/section-view";
import styled from "@emotion/styled";
import { useCallback, useState } from "react";
import LanguageIcon from "@mui/icons-material/Language";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { SinglePhotoUploader } from "@cma/shared/components/SinglePhotoUploader";
import { compressImage } from "@shared/utils/compress-image";
import { useUploadPresentationSectionImage } from "@cma/shared/queries/cma/useUploadPresentationSectionImage";
import { PresentationSectionProps } from "../PresentationSectionProps";
import { TeamBrandingSectionConfig } from "@cma/presentation-generation-logic/models/section-config";
import { EditableInput } from "../../shared/EditableInput";
import { EditableText } from "../../shared/EditableText";
import {
  ContactInfoItem,
  SectionEditWrapper,
  TeamCard,
  TeamLogoContainer,
} from "./TeamBranding.styles";

const NoImagePlaceholder = styled.div`
  width: 100%;
  width: 200px;
  height: 80px;
  background-color: ${(p) => p.theme.colors.background2};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.colors.secondary};
  border-radius: none;
  border-radius: 0.5rem;
`;

interface Props
  extends PresentationSectionProps<
    TeamBrandingSectionView,
    TeamBrandingSectionConfig
  > {
  displayMode: "mobile" | "desktop" | "print" | "tablet";
}

export const TeamBrandingEdit = (props: Props) => {
  const { sectionView, sectionConfig, sourceData, onUpdateSection } = props;

  const { id, name, logo, website } = sectionView;
  const [editModeField, setEditModeField] = useState<string | null>(null);

  const uploadSectionLogoMutation = useUploadPresentationSectionImage({
    onSuccess: (data) => {
      onUpdateSection({
        sectionConfig: { ...sectionConfig, logo: data.url },
      });
    },
  });

  const deleteSectionLogo = useCallback(() => {
    onUpdateSection({
      sectionConfig: { ...sectionConfig, logo: null },
    });
  }, [onUpdateSection, sectionConfig]);

  const uploadSectionOfficeLogo = useCallback(
    async (file: File) => {
      const compressed = await compressImage(file, { preset: "sm" });
      uploadSectionLogoMutation.mutate({
        id: sourceData.id,
        sectionId: sectionConfig.id,
        file: compressed,
      });
    },
    [uploadSectionLogoMutation, sectionConfig, sourceData],
  );

  const onUpdateField = useCallback(
    (fieldName: keyof TeamBrandingSectionConfig, value: string) => {
      onUpdateSection({
        sectionConfig: { ...sectionConfig, [fieldName]: value },
      });
      setEditModeField(null);
    },
    [onUpdateSection, sectionConfig],
  );

  return (
    <SectionEditWrapper id={id}>
      <TeamCard>
        <TeamLogoContainer>
          <SinglePhotoUploader
            onFileAccepted={uploadSectionOfficeLogo}
            onFileRemoved={logo ? deleteSectionLogo : undefined}
            maxSize={5 * 1024 * 1024} // 5MB
            isLoading={uploadSectionLogoMutation.isPending}
            serverError={uploadSectionLogoMutation.error}
            resetServerError={uploadSectionLogoMutation.reset}
          >
            {logo ? (
              <img src={logo} alt={name ?? ""} />
            ) : (
              <NoImagePlaceholder>
                <PhotoCameraIcon fontSize="small" />
              </NoImagePlaceholder>
            )}
          </SinglePhotoUploader>
        </TeamLogoContainer>

        <ContactInfoItem>
          {editModeField !== "name" && !!name ? (
            <EditableText variant="h4" onClick={() => setEditModeField("name")}>
              {name}
            </EditableText>
          ) : (
            <EditableInput
              value={name}
              type="text"
              name="name"
              variant="h4"
              autoFocus={editModeField === "name"}
              onSave={(value) => onUpdateField("name", value)}
              onCancel={() => setEditModeField(null)}
              styles={{ maxWidth: "100%" }}
              placeholder="Team Name"
            />
          )}
        </ContactInfoItem>

        <ContactInfoItem>
          <LanguageIcon fontSize="small" />{" "}
          {editModeField !== "website" && !!website ? (
            <EditableText onClick={() => setEditModeField("website")}>
              {website}
            </EditableText>
          ) : (
            <EditableInput
              value={website}
              type="text"
              name="website"
              autoFocus={editModeField === "website"}
              onSave={(value) => onUpdateField("website", value)}
              onCancel={() => setEditModeField(null)}
              styles={{ maxWidth: "100%" }}
              placeholder="Team Website"
            />
          )}
        </ContactInfoItem>
      </TeamCard>
    </SectionEditWrapper>
  );
};
